import React, { useState } from 'react';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import TimelineItem from './TimelineItem';
import ClipEditModal from './ClipEditModal';
import FramePreviewModal from './FramePreviewModal';
import ConfirmationDialog from './ConfirmationDialog'; // Import ConfirmationDialog

function Timeline({ items, onDragEnd, onRemoveItem, onUpdateText, onUpdateClipTimes, videoUrl, onMoveUp, onMoveDown, onUpdateSceneDescription, onUpdateSceneNarration, onRegenerateAudio, onRegenScript, setIsLoading, setMessage, selectedLanguage, projectId, onQuickPreview, onDeploy, onAddIntroOutro, onDuplicateItem }) {
  const [isClipModalOpen, setIsClipModalOpen] = useState(false);
  const [currentClip, setCurrentClip] = useState(null);
  const [isFramePreviewOpen, setIsFramePreviewOpen] = useState(false);
  const [currentFrameItem, setCurrentFrameItem] = useState(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false); // State for confirmation modal
  const [itemToDelete, setItemToDelete] = useState(null); // State for item to delete

  const handleEditClip = (clip) => {
    setCurrentClip(clip);
    setIsClipModalOpen(true);
  };

  const handleCloseClipModal = () => {
    setIsClipModalOpen(false);
    setCurrentClip(null);
  };

  const handlePreviewFrame = (item) => {
    setCurrentFrameItem(item);
    setIsFramePreviewOpen(true);
  };

  const handleCloseFramePreview = () => {
    setIsFramePreviewOpen(false);
    setCurrentFrameItem(null);
  };

  const handleConfirmRemoveItem = (item) => {
    setItemToDelete(item);
    setIsConfirmModalOpen(true);
  };

  const handleCancelRemove = () => {
    setIsConfirmModalOpen(false);
    setItemToDelete(null);
  };

  const handleConfirmRemove = () => {
    if (itemToDelete) {
      onRemoveItem(itemToDelete.id);
      setIsConfirmModalOpen(false);
      setItemToDelete(null);
    }
  };

  return (
    <div>
      <div className="timeline-controls">
        <div className="timeline-controls-left">
          {onAddIntroOutro && (
            <button onClick={onAddIntroOutro} className="add-intro-outro-button">
              Add Intro/Outro
            </button>
          )}
        </div>
        <div className="timeline-controls-right">
          <button onClick={() => window.location.href = `/project/${projectId}/videos`} className="final-videos-button">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" className="bi bi-collection-play-fill" viewBox="0 0 16 16">
              <path d="M2.5 3.5a.5.5 0 0 1 0-1h11a.5.5 0 0 1 0 1h-11zm2-2a.5.5 0 0 1 0-1h7a.5.5 0 0 1 0 1h-7zM0 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V7zm6 3.5a.5.5 0 0 1 .8-.4l2 1.33a.5.5 0 0 1 0 .84l-2 1.33A.5.5 0 0 1 6 13.5v-3z"/>
            </svg>
            Final Videos
          </button>
          {onDeploy && (
            <button onClick={() => onDeploy()} className="deploy-button">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" className="bi bi-cloud-upload-fill" viewBox="0 0 16 16">
                <path fillRule="evenodd" d="M8 0a5.53 5.53 0 0 0-3.594 1.342c-.766.66-1.321 1.52-1.464 2.383C1.266 4.095 0 5.555 0 7.318 0 9.366 1.708 11 3.781 11H7.5V5.707L5.354 7.854a.5.5 0 1 1-.708-.708l3-3a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1-.708.708L8.5 5.707V11h4.188C14.502 11 16 9.57 16 7.773c0-1.636-1.242-2.969-2.834-3.194C12.923 1.999 10.69 0 8 0z"/>
              </svg>
              Deploy
            </button>
          )}
          <button onClick={() => onQuickPreview && onQuickPreview(items)} className="quick-preview-button">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" className="bi bi-play-circle-fill" viewBox="0 0 16 16">
              <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM6.79 5.093A.5.5 0 0 0 6 5.5v5a.5.5 0 0 0 .79.407l3.5-2.5a.5.5 0 0 0 0-.814l-3.5-2.5z"/>
            </svg>
            Quick Preview
          </button>
        </div>
      </div>
      <DndContext collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={items} strategy={verticalListSortingStrategy}>
          <div className="timeline-container">
            {items.map((item, index) => (
            <TimelineItem
              key={item.id}
              item={item}
              itemIndex={index + 1}
              onConfirmRemoveItem={handleConfirmRemoveItem} // Pass new handler
              onUpdateText={onUpdateText}
              onEditClip={handleEditClip}
              onPreviewFrame={handlePreviewFrame}
              videoUrl={videoUrl}
              onMoveUp={onMoveUp}
              onMoveDown={onMoveDown}
              onUpdateSceneDescription={onUpdateSceneDescription}
              onUpdateSceneNarration={onUpdateSceneNarration}
              onRegenerateAudio={onRegenerateAudio}
              onRegenScript={onRegenScript}
              setIsLoading={setIsLoading}
              setMessage={setMessage}
              selectedLanguage={selectedLanguage}
              projectId={projectId}
              onDuplicateItem={onDuplicateItem}
            />
          ))}
          </div>
        </SortableContext>
      </DndContext>

      {currentClip && (
        <ClipEditModal
          isOpen={isClipModalOpen}
          onClose={handleCloseClipModal}
          clip={currentClip}
          videoUrl={videoUrl}
          onUpdateClipTimes={onUpdateClipTimes}
        />
      )}

      {currentFrameItem && (
        <FramePreviewModal
          isOpen={isFramePreviewOpen}
          onClose={handleCloseFramePreview}
          item={currentFrameItem}
        />
      )}

      {itemToDelete && (
        <ConfirmationDialog
          isOpen={isConfirmModalOpen}
          title="Confirm Deletion"
          message={`Are you sure you want to delete this ${itemToDelete.type}? This action cannot be undone.`}
          onConfirm={handleConfirmRemove}
          onCancel={handleCancelRemove}
        />
      )}
    </div>
  );
}

export default Timeline;
